class AwsmVideoModal extends HTMLElement {
  constructor() {
    super();
    this.videoButton = this.querySelector(".video-section__poster");
    this.videoPopup = this.querySelector(".itg_popup_wr");
    this.popupClose = this.querySelector(".itg_popup_wr .itg_close_btn");
    this.popupOverlay = this.querySelector(".itg_popup_wr .itg_popup_overlay");
    this.lastFocusedElement = null;

    this.onDocumentKeyDown = this.onDocumentKeyDown.bind(this);
    this.onCloseButtonKeyDown = this.onCloseButtonKeyDown.bind(this);
    this.onPopupClick = this.onPopupClick.bind(this);
  }

  connectedCallback() {
    if (this.popupClose) {
      this.popupClose.setAttribute("tabindex", "0");
      this.popupClose.setAttribute("role", "button");
      this.popupClose.setAttribute("aria-label", "Close video popup");
      this.popupClose.addEventListener("click", () => this.closeVideoPopup());
      this.popupClose.addEventListener("keydown", this.onCloseButtonKeyDown);
    }

    if (this.popupOverlay) {
      this.popupOverlay.addEventListener("click", this.onPopupClick);
    }

    if (this.videoPopup) {
      this.videoPopup.setAttribute("role", "dialog");
      this.videoPopup.setAttribute("aria-modal", "true");
      this.videoPopup.setAttribute("aria-hidden", "true");
    }

    if (this.videoButton) {
      this.videoButton.addEventListener("click", () => this.openVideoPopup());
      this.videoButton.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          this.openVideoPopup();
        }
      });
    }

    // The YouTube iframe is cross-origin: once it takes keyboard focus, Escape
    // and Tab keydowns fire inside its own document and never reach us here,
    // so the modal stops responding to the keyboard entirely. Keeping it out
    // of the Tab order (mouse clicks still work fine) keeps every keypress
    // inside this document while the dialog is open.
    const videoFrame = this.videoPopup
      ? this.videoPopup.querySelector(".js-youtube")
      : null;
    if (videoFrame) {
      videoFrame.setAttribute("tabindex", "-1");
    }
  }

  openVideoPopup() {
    if (!this.videoPopup) return;
    const videoFrame = this.videoPopup.querySelector(".js-youtube");
    if (!videoFrame) return;

    this.lastFocusedElement = document.activeElement;
    this.videoPopup.classList.add("visible");
    const videoUrl = videoFrame.getAttribute("data-src");
    videoFrame.setAttribute("src", `${videoUrl}&autoplay=1`);
    document.body.style.overflow = "hidden";
    this.videoPopup.setAttribute("aria-hidden", "false");
    if (this.videoButton) {
      this.videoButton.setAttribute("aria-expanded", "true");
    }

    document.addEventListener("keydown", this.onDocumentKeyDown);
    if (typeof trapFocus === "function") {
      trapFocus(this.videoPopup, this.popupClose || this.videoPopup);
    } else if (this.popupClose) {
      this.popupClose.focus();
    }
  }

  onPopupClick(event) {
    if (!event.target.closest(".itg_popup_overlay")) return;
    this.closeVideoPopup();
  }

  onCloseButtonKeyDown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.closeVideoPopup();
    }
  }

  onDocumentKeyDown(event) {
    if (event.code.toUpperCase() === "ESCAPE") {
      this.closeVideoPopup();
    }
  }

  closeVideoPopup() {
    if (!this.videoPopup) return;
    const videoFrame = this.videoPopup.querySelector(".js-youtube");
    if (videoFrame) {
      videoFrame.setAttribute("src", "");
    }

    this.videoPopup.classList.remove("visible");
    document.body.style.overflow = "auto";
    this.videoPopup.setAttribute("aria-hidden", "true");
    if (this.videoButton) {
      this.videoButton.setAttribute("aria-expanded", "false");
    }

    document.removeEventListener("keydown", this.onDocumentKeyDown);
    if (typeof removeTrapFocus === "function") {
      removeTrapFocus(this.lastFocusedElement);
    }

    if (
      this.lastFocusedElement &&
      typeof this.lastFocusedElement.focus === "function"
    ) {
      this.lastFocusedElement.focus();
    }
  }
}

if (!customElements.get("awsm-videomodal")) {
  customElements.define("awsm-videomodal", AwsmVideoModal);
}
